'use client'

import { memo } from 'react'
import { Check, X } from 'lucide-react'
import { cn } from '@/lib/utils'

export const GOVTS_SCORECARD_ROWS = [
  { feature: 'Citizen data stays in-country', cloud: false, legacy: true, ninx: true },
  { feature: 'Agentic department workflows', cloud: false, legacy: false, ninx: true },
  { feature: 'Air-gapped ministry deployment', cloud: false, legacy: true, ninx: true },
  { feature: 'Full audit trail for oversight', cloud: false, legacy: false, ninx: true },
  { feature: 'Modern reasoning without cloud lock-in', cloud: true, legacy: false, ninx: true },
] as const

const ScoreMark = ({ pass, highlight }: { pass: boolean; highlight?: boolean }) =>
  pass ? (
    <Check className={cn('h-4 w-4', highlight ? 'text-[#21728d]' : 'text-emerald-600')} aria-label="Pass" />
  ) : (
    <X className="h-4 w-4 text-rose-500/70" aria-label="Fail" />
  )

const ScorecardPanel = ({
  title,
  subtitle,
  variant,
  rows,
  highlight,
}: {
  title: string
  subtitle: string
  variant: 'cloud' | 'legacy' | 'ninx'
  rows: typeof GOVTS_SCORECARD_ROWS
  highlight?: boolean
}) => {
  const key = variant === 'cloud' ? 'cloud' : variant === 'legacy' ? 'legacy' : 'ninx'
  const passCount = rows.filter((row) => row[key]).length

  return (
    <div
      className={cn(
        'ninxl-govts-scorecard ninxl-pixel-border flex h-full flex-col p-5',
        highlight && 'ninxl-govts-scorecard--winner',
      )}
    >
      <div className="mb-4 border-b-2 border-[color:var(--ninxl-8bit-dim)] pb-4">
        <div className="ninxl-8bit-label mb-1 text-[9px]">{subtitle}</div>
        <h3 className={cn('text-base font-bold', highlight ? 'ninxl-theme-text-accent' : 'ninxl-theme-text')}>
          {title}
        </h3>
        <div className="ninxl-8bit-label ninxl-theme-text-muted mt-2 text-[10px]">
          {passCount}/{rows.length} criteria met
        </div>
      </div>
      <ul className="flex-1 space-y-3">
        {rows.map((row) => (
          <li key={row.feature} className="flex items-start gap-3">
            <ScoreMark pass={row[key]} highlight={highlight} />
            <span className="ninxl-theme-text-muted text-xs leading-relaxed">{row.feature}</span>
          </li>
        ))}
      </ul>
      {highlight ? (
        <div className="ninxl-govts-scorecard-stamp mt-5 text-center">
          <span className="ninxl-8bit-label text-[9px]">RECOMMENDED FOR GOVTS</span>
        </div>
      ) : null}
    </div>
  )
}

/** Three-panel scorecard manifest, not a spreadsheet table */
export const NinxlGovtsScorecard = memo(function NinxlGovtsScorecard({ className }: { className?: string }) {
  return (
    <div className={cn('grid gap-4 lg:grid-cols-3', className)} id="govts-scorecard">
      <ScorecardPanel
        title="Foreign Cloud SaaS"
        subtitle="VENDOR STACK"
        variant="cloud"
        rows={GOVTS_SCORECARD_ROWS}
      />
      <ScorecardPanel
        title="Legacy On-Prem"
        subtitle="LEGACY STACK"
        variant="legacy"
        rows={GOVTS_SCORECARD_ROWS}
      />
      <ScorecardPanel
        title="NINX Govts"
        subtitle="SOVEREIGN STACK"
        variant="ninx"
        rows={GOVTS_SCORECARD_ROWS}
        highlight
      />
    </div>
  )
})